import * as PIXI from "pixi.js";

class WebGLSmoke {
  constructor(app) {
    this.app = app;
    this.container = new PIXI.Container();
    this.smokeClouds = [];
    this.time = 0;
    this.init();
  }

  async init() {
    // Create a smoke texture programmatically since we don't have image assets
    this.smokeTexture = this.createSmokeTexture();

    // Create fewer, larger fog clouds for more realistic fog
    const fogCount = 25;

    for (let i = 0; i < fogCount; i++) {
      this.createFogCloud();
    }

    // Add blur filter to make it look more like fog
    const blurFilter = new PIXI.BlurFilter(3, 6);
    this.container.filters = [blurFilter];

    this.updateBound = () => this.update();
    this.app.ticker.add(this.updateBound);
  }

  createSmokeTexture() {
    // Create a canvas to draw a more organic smoke shape
    const canvas = document.createElement("canvas");
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext("2d");

    // Create a radial gradient that looks more like smoke
    const gradient = ctx.createRadialGradient(64, 64, 20, 64, 64, 64);
    gradient.addColorStop(0, "rgba(200, 200, 200, 0.8)");
    gradient.addColorStop(0.3, "rgba(180, 180, 180, 0.6)");
    gradient.addColorStop(0.6, "rgba(160, 160, 160, 0.3)");
    gradient.addColorStop(1, "rgba(140, 140, 140, 0)");

    ctx.fillStyle = gradient;

    // Draw multiple overlapping circles for irregular shape
    for (let i = 0; i < 8; i++) {
      const offsetX = (Math.random() - 0.5) * 40;
      const offsetY = (Math.random() - 0.5) * 40;
      const size = Math.random() * 30 + 25;

      ctx.beginPath();
      ctx.arc(64 + offsetX, 64 + offsetY, size, 0, Math.PI * 2);
      ctx.fill();
    }

    return PIXI.Texture.from(canvas);
  }

  createFogCloud() {
    // Use sprite with our custom smoke texture
    const cloud = new PIXI.Sprite(this.smokeTexture);

    // Random size for variety
    const baseScale = Math.random() * 1.5 + 0.8;
    cloud.scale.set(baseScale);

    // Center anchor point
    cloud.anchor.set(0.5, 0.5);

    // Random tint for color variation
    const fogColors = [0xaaaaaa, 0xbbbbbb, 0xcccccc, 0xdddddd, 0xeeeeee];
    cloud.tint = fogColors[Math.floor(Math.random() * fogColors.length)];

    // Starting position - from bottom of screen like a fog machine
    cloud.x = Math.random() * this.app.screen.width;
    cloud.y = this.app.screen.height - Math.random() * 30;

    // Random rotation for more variety
    cloud.rotation = Math.random() * Math.PI * 2;

    // Movement properties - fog flows upward and spreads
    cloud.vx = (Math.random() - 0.5) * 1.2;
    cloud.vy = -Math.random() * 1.8 - 0.8;
    cloud.life = 1.0;
    cloud.decay = Math.random() * 0.004 + 0.002;
    cloud.baseScale = baseScale;
    cloud.growthRate = Math.random() * 2 + 1;
    cloud.rotationSpeed = (Math.random() - 0.5) * 0.005;

    // Horizontal spreading as it rises
    cloud.wavePhase = Math.random() * Math.PI * 2;
    cloud.waveSpeed = Math.random() * 0.015 + 0.008;

    this.smokeClouds.push(cloud);
    this.container.addChild(cloud);
  }

  update() {
    this.time += 0.016;

    this.smokeClouds.forEach((cloud, index) => {
      // Add wavy horizontal motion like real fog
      cloud.wavePhase += cloud.waveSpeed;
      const waveOffset = Math.sin(cloud.wavePhase) * 0.5;

      cloud.x += cloud.vx + waveOffset;
      cloud.y += cloud.vy;

      // Fog spreads horizontally as it rises
      cloud.vx += (Math.random() - 0.5) * 0.05; // Random horizontal drift
      cloud.vx *= 0.98; // Gradual slowdown
      cloud.vy *= 0.995; // Slight upward momentum loss

      // Life decay
      cloud.life -= cloud.decay;

      // Reset fog cloud when it dies or goes off screen (fog machine keeps producing)
      if (cloud.life <= 0 || cloud.y < -150) {
        cloud.x = Math.random() * this.app.screen.width;
        cloud.y = this.app.screen.height - Math.random() * 30;
        cloud.vx = (Math.random() - 0.5) * 1.2;
        cloud.vy = -Math.random() * 1.8 - 0.8;
        cloud.life = 1.0;
        cloud.wavePhase = Math.random() * Math.PI * 2;
        cloud.rotation = Math.random() * Math.PI * 2;

        // Randomize tint and scale for variety
        const fogColors = [0xaaaaaa, 0xbbbbbb, 0xcccccc, 0xdddddd, 0xeeeeee];
        cloud.tint = fogColors[Math.floor(Math.random() * fogColors.length)];
        cloud.baseScale = Math.random() * 1.5 + 0.8;
      }

      // Slow rotation for organic movement
      cloud.rotation += cloud.rotationSpeed;

      // Update visual properties for realistic fog
      const lifeRatio = cloud.life;

      // Fog expands as it rises
      const scale = cloud.baseScale * (1 + (1 - lifeRatio) * cloud.growthRate);
      cloud.scale.set(scale);

      // Fade out gradually - fog is more transparent
      cloud.alpha = lifeRatio * 0.25; // Lower opacity for realistic fog

      // Wrap horizontally for continuous fog effect
      if (cloud.x < -150) cloud.x = this.app.screen.width + 150;
      if (cloud.x > this.app.screen.width + 150) cloud.x = -150;
    });
  }

  destroy() {
    try {
      if (this.updateBound && this.app && this.app.ticker) {
        this.app.ticker.remove(this.updateBound);
        this.updateBound = null;
      }

      this.smokeClouds = [];

      if (this.container) {
        this.container.destroy({ children: true, texture: true });
        this.container = null;
      }
    } catch (e) {
      console.warn("Error destroying WebGLSmoke:", e);
    }
  }
}

export default WebGLSmoke;
